import { Button } from 'react-bootstrap';
import useLeadsStore from '../../../store/LeadsStore';
import {
	RoleEnum,
	TabsEnum,
	LeadsModalModeEnum,
	LeadStatusEnum,
} from '../../../api/models/Enums';
import { useTranslation } from 'react-i18next';
import toaster from '../../../utils/toaster';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import { useApiClient } from '../../../hooks/useApiClient';
import { useAuth } from '../../../hooks/useAuth';
import useModalStore from '../../../store/ModalStore';
import SpinnerButton from '../../SpinnerButton/SpinnerButton';
import { useErrorToast } from '../../../hooks/useErrorToast';
import { ClienteEnum } from '../../../api/models/Enums';
import '../../../pages/Leads/LeadsPage.scss';
import { useMemo, useEffect, useState } from 'react';

const LeadActionButtons = () => {
	const queryClient = useQueryClient();
	const { apiClient } = useApiClient();
	const { activeTab, activeLead } = useLeadsStore();
	const { t } = useTranslation();
	const { closeModal, openModal } = useModalStore();
	const { userRole } = useAuth();
	const { showError } = useErrorToast();

	const { mutate: saveNurtureLead, isPending } = useMutation({
		mutationFn: (leadId: string) =>
			apiClient.leadsControllerService.nurtureLead(leadId),
		onSuccess: (data) => {
			toaster.success(t('lead_nurtured_successfully'));
			queryClient.invalidateQueries({ queryKey: ['leads_list'] });
			closeModal();
		},
		onError: (error) => {
			console.error(i18next.t('error_nurturing_lead'), error);
			showError(error, i18next.t('error_nurturing_lead'));
		},
	});

	const { mutate: changeStateToQuoted, isPending: isPendingQuoted } =
		useMutation({
			mutationFn: (leadId: string) =>
				apiClient.leadsControllerService.convertLead(
					leadId,
					LeadStatusEnum.QUOTED
				),

			onSuccess: (data) => {
				toaster.success(t('lead_moved_to_quoted_successfully'));
				queryClient.invalidateQueries({ queryKey: ['leads_list'] });
				closeModal();
			},
			onError: (error) => {
				console.error(i18next.t('error_quoting_lead'), error);
				toaster.error(i18next.t('error_quoting_lead'));
			},
		});

	const handleNurturinglead = () => {
		if (activeLead && activeLead.Id) {
			saveNurtureLead(activeLead.Id);
		} else {
			console.error('lead_Id_not_found');
			showError({}, i18next.t('lead_Id_not_found'));
		}
	};

	const handleMoveToQuoted = () => {
		if (activeLead && activeLead.Id) {
			changeStateToQuoted(activeLead.Id);
		} else {
			console.error('lead_Id_not_found');
			showError({}, i18next.t('lead_Id_not_found'));
		}
	};

	const handleOpenConvertLeadModal = () => {
		if (activeLead.Cliente__c === ClienteEnum.Azienda) {
			if (!activeLead.Partita_Iva__c || !activeLead.Company) {
				showError(null, i18next.t('fill_all_required_field'));
				return;
			}
		}
		if (activeLead.Cliente__c === ClienteEnum.Libero_Professionista) {
			if (
				!activeLead.Partita_Iva__c ||
				!activeLead.FirstName ||
				!activeLead.LastName
			) {
				showError(null, i18next.t('fill_all_required_field'));
				return;
			}
		}
		if (activeLead.Cliente__c === ClienteEnum.Privato) {
			if (
				!activeLead.Codice_Fiscale__c ||
				!activeLead.FirstName ||
				!activeLead.LastName
			) {
				showError(null, i18next.t('fill_all_required_field'));
				return;
			}
		}
		openModal(LeadsModalModeEnum.QUALIFIED);
	};

	const canNurtureLead = useMemo(() => {
		const allowedRoles = [
			RoleEnum.ADMIN,
			RoleEnum.SALES,
			RoleEnum.SALES_DEALER,
			RoleEnum.SALES_INTERNAL,
			RoleEnum.BACKOFFICE,
			RoleEnum.MASTER,
			RoleEnum.AREA_MANAGER,
		];

		return (
			activeLead?.Modello__c &&
			(activeTab === TabsEnum.NEW_LEADS ||
				activeTab === TabsEnum.UNQUALIFIED_LEADS) &&
			allowedRoles.includes(userRole)
		);
	}, [activeLead?.Modello__c, activeTab, userRole]);

	const isNurtureDisabled = useMemo(() => {
		return !activeLead?.Modello__c;
	}, [activeLead?.Modello__c]);

	const isNurtureLeadButtonVisible = useMemo(() => {
		return (
			activeTab === TabsEnum.NEW_LEADS ||
			activeTab === TabsEnum.UNQUALIFIED_LEADS
		);
	}, [activeTab]);

	return (
		<div>
			{/* BTN presa in carico */}
			{isNurtureLeadButtonVisible ? (
				<div className="d-flex align-items-end">
					{!canNurtureLead || isNurtureDisabled ? (
						<p className="last_modified_date my-0 mt-4 mx-2">
							{t('lead_nurture_model_fill_required')}
						</p>
					) : null}

					<Button
						className="btn"
						onClick={handleNurturinglead}
						disabled={!canNurtureLead || isNurtureDisabled || isPending}
					>
						<SpinnerButton
							label={t('nurturing_lead').toUpperCase()}
							busy={isPending}
							loadingText={t('loading')}
						/>
					</Button>
				</div>
			) : null}

			{activeTab === TabsEnum.NURTURE_LEADS && (
				<div>
					<Button
						variant="primary"
						onClick={() => {
							handleMoveToQuoted();
						}}
						className="me-2"
					>
						<SpinnerButton label={i18next.t('move_to_quotation_sent')} />
					</Button>

					<Button
						variant="primary"
						onClick={() => {
							openModal(LeadsModalModeEnum.UNQUALIFIED);
						}}
						className="me-2"
					>
						<SpinnerButton label={i18next.t('unqualify_lead')} />
					</Button>
				</div>
			)}

			{activeTab === TabsEnum.TO_BE_CONVERTED && (
				<div>
					<Button
						variant="primary"
						onClick={() => {
							handleOpenConvertLeadModal();
						}}
						className="me-2"
					>
						<SpinnerButton label={i18next.t('convert_lead')} />
					</Button>

					<Button
						variant="primary"
						onClick={() => {
							openModal(LeadsModalModeEnum.UNQUALIFIED);
						}}
						className="me-2"
					>
						<SpinnerButton label={i18next.t('unqualify_lead')} />
					</Button>
				</div>
			)}
		</div>
	);
};

export default LeadActionButtons;
